import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Box, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useCallback, useEffect, useMemo, useState, } from 'react';
import { ErrorBoundary } from 'shared/components/error-boundary';
import { preventDefault, useBox, useCallbacks } from 'shared/hooks/utils';
import { useAction } from 'shared/hooks/redux/hooks';
import { cleanICCID, cleanMSISDN } from 'console/sim/utils';
import { PhoneNumberField } from 'console/shared/components/fields/phone-number';
import { ControlWithoutValidation } from 'console/shared/components/fields/control/without-validation';
import { TextInputWrapper } from 'console/shared/components/fields/text';
import { useMutationCallback } from 'shared/api/hooks';
import { changeSimInDevice, update_billed_sim, update_foreign_sim, } from 'console/sim/redux/actions';
import { DocumentScanner } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router';
import { useBooleanState } from 'shared/hooks/state/boolean';
function catchError(func) {
    try {
        const result = func();
        return [result, null];
    }
    catch (error) {
        return [null, error];
    }
}
export function RegisterSimForm(props) {
    const { for_device_id, onAddBilledSim, onDone } = props;
    const [msisdn, setMSISDN] = useState('+7');
    const boxedMsisdn = useBox(msisdn);
    const [iccid, setICCID] = useState('8970101');
    const boxedIccid = useBox(iccid);
    const [error, setError] = useState(null);
    const [show_iccid, , setShowICCID, toggleIccid] = useBooleanState(false);
    const boxedShowIccid = useBox(show_iccid);
    const [onRegisterSim, loading_request, response, request_error, is_send_request] = useMutationCallback('sim/register_sim');
    const onUpdateBilledSim = useAction(update_billed_sim);
    const onUpdateForeignSim = useAction(update_foreign_sim);
    const onUpdateSimInDevice = useAction(changeSimInDevice);
    const onSubmit = useCallback((response) => {
        let sim_in_device = null;
        if (response.type === 'billed' && response.billed_sim != null) {
            onUpdateBilledSim(response.billed_sim);
            onAddBilledSim?.(response.billed_sim);
            sim_in_device = {
                sim_type: 'billed',
                sim_id: response.billed_sim.sim_card.id,
            };
        }
        if (response.type === 'foreign' && response.foreign_sim != null) {
            onUpdateForeignSim(response.foreign_sim);
            sim_in_device = {
                sim_type: 'foreign',
                foreign_msisdn: response.foreign_sim.msisdn,
            };
        }
        if (sim_in_device != null) {
            if (for_device_id != null) {
                onUpdateSimInDevice(for_device_id, sim_in_device);
            }
            if (sim_in_device.sim_type == 'foreign') {
                onDone?.(sim_in_device.foreign_msisdn);
                return;
            }
        }
    }, [for_device_id, onAddBilledSim, onDone]);
    useEffect(() => {
        if (is_send_request && response != null) {
            onSubmit(response);
        }
    }, [response]);
    const onChangeButton = useCallbacks([preventDefault, toggleIccid]);
    const onTryRegisterSim = useCallback(() => {
        setError(null);
        const [msisdn, msisdn_error] = catchError(() => cleanMSISDN(boxedMsisdn.current));
        const [iccid, iccid_error] = catchError(() => cleanICCID(boxedIccid.current));
        let error;
        if (boxedShowIccid.current) {
            if (boxedIccid.current) {
                error = iccid_error;
            }
            else {
                error = msisdn_error;
            }
        }
        else {
            error = msisdn_error;
        }
        if (error) {
            setError(error);
            return;
        }
        setICCID(iccid);
        setMSISDN(msisdn);
        onRegisterSim({ msisdn, iccid });
    }, []);
    const onClickOk = useCallbacks([preventDefault, onTryRegisterSim]);
    const onScanClick = useCallback((event) => {
        preventDefault(event);
        if (window.cordova?.plugins?.barcodeScanner == null) {
            setError('Ваше устройство не поддерживает сканирование штрих-кодов');
            return;
        }
        window.cordova.plugins.barcodeScanner.scan((result) => {
            if (result.cancelled) {
                return;
            }
            console.log(`Got bar-code: ${result.text}`);
            setICCID(result.text);
            boxedIccid.current = result.text;
            setShowICCID();
            boxedShowIccid.current = true;
            onTryRegisterSim();
        }, (error) => {
            console.log(`Failed to scan bar-code: ${error}`);
        }, {
            resultDisplayDuration: 1,
            showFlipCameraButton: true,
            showTorchButton: true,
            prompt: 'Наведите на штрих-код\nна сим-карте',
            formats: 'CODE_128',
            disableAnimations: false,
        });
    }, [onTryRegisterSim]);
    return (_jsx(ErrorBoundary, { children: _jsxs(Box, { display: "flex", flexDirection: "column", gap: 2, p: 2, width: "100%", children: [window.cordova?.plugins.barcodeScanner && (_jsx(Button, { onClick: onScanClick, startIcon: _jsx(DocumentScanner, {}), variant: "contained", color: "secondary", children: "\u041E\u0442\u0441\u043A\u0430\u043D\u0438\u0440\u043E\u0432\u0430\u0442\u044C" })), _jsx(ControlWithoutValidation, { label: "\u041D\u043E\u043C\u0435\u0440 \u0442\u0435\u043B\u0435\u0444\u043E\u043D\u0430 \u0441\u0438\u043C-\u043A\u0430\u0440\u0442\u044B \u0432 \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u0435", value: msisdn ?? '+7', onChange: setMSISDN, help_id: "sim_msisdn", field: PhoneNumberField }), _jsx(Button, { variant: "outlined", onClick: onChangeButton, children: "\u041D\u0430 \u0441\u0438\u043C-\u043A\u0430\u0440\u0442\u0435 \u043D\u0435 \u0443\u043A\u0430\u0437\u0430\u043D \u043D\u043E\u043C\u0435\u0440" }), show_iccid && (_jsx(_Fragment, { children: _jsx(ControlWithoutValidation, { label: "20-\u0437\u043D\u0430\u0447\u043D\u044B\u0439 \u043D\u043E\u043C\u0435\u0440 \u0441\u0438\u043C-\u043A\u0430\u0440\u0442\u044B", value: iccid ?? '8970101', onChange: setICCID, help_id: "sim_msisdn", field: (input_props) => (_jsx(TextInputWrapper, { placeholder: "8970101xxxxxxxxxxxx", ...input_props })) }) })), error && _jsx(Alert, { severity: "error", children: error }), request_error && _jsx(Alert, { severity: "error", children: request_error }), _jsx(LoadingButton, { color: "primary", onClick: onClickOk, disabled: loading_request, variant: "contained", children: "\u0414\u0430\u043B\u0435\u0435" })] }) }));
}
export function RegisterSimFormRouter() {
    const { state } = useLocation();
    const device_id = useMemo(() => {
        if (state && state.device_id) {
            return parseInt(state.device_id, 10);
        }
        return undefined;
    }, [state]);
    const navigate = useNavigate();
    const onAddBilledSim = useCallback((billed_sim) => {
        navigate(`/sim/billed/${billed_sim.sim_card.id.id}/${billed_sim.sim_card.id.operator}/pay`);
    }, []);
    const onAddForeignSim = useCallback((msisdn) => {
        navigate(`/sim/foreign/${msisdn}`);
    }, []);
    return (_jsx(RegisterSimForm, { for_device_id: device_id, onAddBilledSim: onAddBilledSim, onDone: onAddForeignSim }));
}
